import request from 'sb-request'
import actions from 'redux/actions'
import { getState, reducers } from 'redux/core'
import { getFeatureToggle } from 'helpers'
import { modifySubscription } from 'common/helpers/modifiers/modifyResponse'
import type { ISubscriptionOutput } from 'common/helpers/modifiers/modifyResponse'
import { constants, fbq, getGqlQuery, links, mixpanel, sentry, dataLayer, object } from 'common/helpers'
import { getUpgradePlans, getSubscriptionPlanCosts, skipSubscription, changeSubscriptionPlan,
  subscriptionUnsubscribe, getChangeFrequencyPlans, resubscribe as resubscribeAction,
  unskipSubscription, getPlanSizes, getSubscriptionCoupon as getSubscriptionCouponAction } from 'actions/subscription'
import { applyCoupon as applyCouponAction } from 'actions/coupon'
import { BusinessErrorCode, OriginType, UnskipErrorCode } from 'typings/graphql'
import type { SubscriptionUnsubscribeResult } from 'actions/subscription'
import type { UnpauseTypes } from 'common/helpers/constants/unpauseTypes'
import { openErrorModal } from 'compositions/modals/ErrorModal/ErrorModal'

import availablePlansToUpgradeQuery from './availablePlansToUpgrade.graphql'
import subscriptionQuery from './subscription.graphql'
import skipQuery from './skip.graphql'
import planSizesQuery from './plansSizes.graphql'
import frequenciesQuery from './frequencies.graphql'
import couponQuery from './coupon.graphql'


const set = (subscription: ISubscriptionOutput) => {
  const { isSubscribed } = subscription

  reducers.models.subscription.update(subscription)

  // resolve postponed LEP
  if (isSubscribed) {
    // Try to add postponed limited items for user after subscription
    actions.models.queue.resolveAllPostponedLimitedItems({ withoutErrorHandler: true })
  }

  // set cookie for gtm attentive popup
  actions.misc.cookies.setSessionItem(constants.cookieNames.pastPurchaser, isSubscribed ? 1 : 0)
}

const fetch = (awaitFor?: Promise<any>): Promise<ISubscriptionOutput> =>
  // ATTN this graphql used for Recurly too!
  request.post('graphql?name=subscription', {
    body: {
      query: subscriptionQuery,
    },
    modifyResult: (data) => modifySubscription(data.user.subscription),
  })
    .then((result: ISubscriptionOutput) => {
      if (awaitFor) {
        awaitFor.then(() => set(result))
      }
      else {
        set(result)
      }

      return result
    })

const fetchCoupon = () => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    return getSubscriptionCouponAction()
      .then(({ data, error }) => {
        if (error) {
          console.error(error)

          openErrorModal({ error: error.message })

          return Promise.reject()
        }

        return data // coupon code
      })
  }

  return request.post('graphql?name=getSubscriptionCoupon', {
    body: {
      query: couponQuery,
    },
    modifyResult: (data) => object.get(data, 'user.subscription.coupon'),
  })
}


// ------------------------------------------------------------------------------------------

const _fetchRecurlyPlans = async () => {
  try {
    const { data: plans } = await getUpgradePlans()

    const groupsByCount = []
    let finalPlans = plans.map(({ plan, ...rest }) => ({ ...rest, ...plan }))

    finalPlans.forEach((plan) => {
      if (plan && plan.productsPerPeriod) {
        const modifiedPlan = {
          ...plan,
          totalPrice: plan.price, // TODO Recurly. use "price" instead when Recurly will be ON - added on 5/25/20 by pavelivanov
        }

        const currentGroup = groupsByCount.find(({ scentCount } = {}) => scentCount === modifiedPlan.productsPerPeriod)

        if (!currentGroup) {
          groupsByCount.push({
            scentCount: modifiedPlan.productsPerPeriod,
            plans: [ modifiedPlan ],
          })
        }
        else {
          currentGroup.plans.push(modifiedPlan)
        }
      }
      else {
        sentry.captureException(`model.subscription.actions.fetchPlans plan does not have required scentCount prop`, {
          extra: {
            plans,
          },
        })
      }
    })

    groupsByCount.sort((a, b) => a.scentCount - b.scentCount)

    reducers.models.subscription.setPlans(groupsByCount)

    return groupsByCount
  }
  catch (error) {
    console.error(error)

    return Promise.reject()
  }
}

const _fetchVindiciaPlans = () =>
  request.post('graphql?name=availablePlansToUpgrade', {
    body: {
      query: availablePlansToUpgradeQuery,
    },
    modifyResult: ({ plans }) => {
      plans = plans || []

      const groupsByCount = []

      plans
        .forEach((plan) => {
          if (plan && plan.scentCount) {
            const currrentGroup = groupsByCount.find(({ scentCount } = {}) => scentCount === plan.scentCount)

            if (!currrentGroup) {
              groupsByCount.push({
                scentCount: plan.scentCount,
                plans: [ plan ],
              })
            }
            else {
              currrentGroup.plans.push(plan)
            }
          }
          else {
            sentry.captureException(`model.subscription.actions.fetchPlans plan does not have required scentCount prop`, {
              extra: {
                plans,
              },
            })
          }
        })

      return groupsByCount
    },
  })
    .then((result) => {
      reducers.models.subscription.setPlans(result)

      return result
    })

const fetchPlans = () => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    return _fetchRecurlyPlans()
  }

  return _fetchVindiciaPlans()
}


// ------------------------------------------------------------------------------------------

const _fetchRecurlyPlansSizes = async (values: FetchPlansSizesValues) => {
  try {
    const { data: plans } = await getPlanSizes(values)

    return plans.map(({ name, billingPeriod, shippingPeriod, productsPerPeriod, price, save }) => ({
      planName: name,
      size: billingPeriod,
      shippingPeriod,
      products: productsPerPeriod,
      price,
      save,
    }))
  }
  catch (error) {
    console.error(error)

    return Promise.reject()
  }
}

type PlansSizesVariables = {
  input: {
    products?: number
    shipping?: number
    billing?: number
  }
}

type PlansSizesResponse = {
  plansQuery: {
    planName: string
    size: number
    shippingPeriod: number
    products: number
    price: string
    save: string
  }[]
}

type PlansSizesModified = PlansSizesResponse['plansQuery']

const _fetchVindiciaPlansSizes = ({ productsPerPeriod, shippingPeriod, billingPeriod }: FetchPlansSizesValues) =>
  request.gql<PlansSizesVariables, PlansSizesResponse, PlansSizesModified>('plansQuery', {
    body: {
      query: planSizesQuery,
      variables: {
        input: {
          products: productsPerPeriod,
          shipping: shippingPeriod,
          billing: billingPeriod,
        },
      },
    },
    modifyResult: ({ plansQuery }) => plansQuery || [],
  })

type FetchPlansSizesValues = {
  productsPerPeriod?: number
  shippingPeriod?: number
  billingPeriod?: number
}

const fetchPlansSizes = (values: FetchPlansSizesValues = {}): Promise<PlansSizesModified> => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    return _fetchRecurlyPlansSizes(values)
  }

  return _fetchVindiciaPlansSizes(values)
}


// ---------------------------------------------------------------------------------------------------

const _fetchRecurlyFrequencies = async (): Promise<FrequenciesModified> => {
  try {
    const { data: plans, error } = await getChangeFrequencyPlans()

    if (error) {
      openErrorModal({ error: error.message })

      return Promise.reject(error)
    }

    return plans.map(({ plan, nextBillingDate, nextShippingDate }) => ({
      ...plan,
      nextBillingDate,
      nextShippingDate,
    }))
  }
  catch (axiosError) {
    console.error(axiosError)

    return Promise.reject()
  }
}

type FrequenciesResponse = {
  frequencies: {
    list: {
      selected: boolean
      plan: {
        name: string
        billingPeriod: number
        shippingPeriod: number
      }
      nextBillingDate: string
      nextShippingDate: {
        from: string
        to: string
      }
    }[]
  }
}

type FrequenciesModified = Array<{
  name: string
  billingPeriod: number
  shippingPeriod: number
  nextBillingDate: string
  nextShippingDate: {
    from: string
    to: string
  }
  selected?: boolean
}>

const _fetchVindiciaFrequencies = (): Promise<FrequenciesModified> => (
  request.gql<void, FrequenciesResponse, FrequenciesModified>('frequencies', {
    body: {
      query: frequenciesQuery,
    },
    modifyResult: ({ frequencies: { list } }) => (
      list.map(({ plan, ...rest }) => ({ ...plan, ...rest }))
    ),
    withNestedErrors: true,
  })
)

const fetchFrequencies = (): Promise<FrequenciesModified> => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    return _fetchRecurlyFrequencies()
  }

  return _fetchVindiciaFrequencies()
}


// ---------------------------------------------------------------------------------------------------

// TODO replace with graphql - added on 2020-05-22 by maddoger
const fetchFrequenciesForResubscribeUser = () =>
  request.get('rest/resubscribe-reminder-popup/frequencies')
    .then((result) => Object.values(result))

const fetchCurrentPlan = () =>
  request.get('rest/subscription/plan')
    .then((result) => reducers.models.subscription.setCurrentPlan(result))

const fetchCurrentPlanProductPrice = () => {
  const { models: { subscription: { currPlan } } } = getState()
  const { billingPeriod, shippingPeriod, productsPerPeriod } = currPlan

  if (!billingPeriod || !shippingPeriod || !productsPerPeriod) {
    return Promise.resolve()
  }

  const plansQuery = getGqlQuery('plansQuery', {
    products: productsPerPeriod,
    shipping: shippingPeriod,
    billing: billingPeriod,
  })

  return request.post('graphql?name=planQuery', {
    body: {
      query: `{
        plan: ${plansQuery} {
          productPrice
        }
      }`,
    },
    modifyResult: ({ plan }) => plan && plan[0] ? plan[0].productPrice : null,
  })
    .then((productPrice) => {
      reducers.models.subscription.setCurrentPlan({ ...currPlan, productPrice })

      return productPrice
    })
}

const updateStatus = (status) => reducers.models.subscription.update({ status })


// ------------------------------------------------------------------------------------------

const _changeRecurlyPlan = async (planName: string, placement: string) => {
  // ATTN there is one place in project where resolve .then((error) => handled
  //  and multiple places where reject handled as ({ error }) =>
  try {
    const { session, data, error } = await changeSubscriptionPlan({
      targetPlan: planName,
      metadata: {
        placement,
      },
    })

    const { nextBillingDate /* Date */, plan: { id, name }, shipmentPeriod: { from, to } } = data

    actions.models.queue.fetch()

    if (error) {
      // TODO this logic taken from _changeVindiciaPlan but in Recurly realization we don't have same error code,
      //  so need to ask Business if we need save this logic and if yes then in what case we should redirect user
      //  to Card Update - added on 5/21/20 by pavelivanov
      // actions.models.router.push(links.cardUpdate)

      return Promise.reject(error)
    }

    try {
      // if fetch() fails we shouldn't pass errors from it to changePlan() handlers
      // it's possible that error will be added to parent try catch Promise.reject()
      await fetch()
    }
    catch {
      return Promise.reject()
    }
  }
  catch (error) {
    console.error(error)
    // TODO do we need redirect here?
    //  https://docs.google.com/spreadsheets/u/1/d/17yPDvSxgduDe9It4LjjMvafj-Id9hTIr-MJq1r5MZCg/edit#gid=0
    //  - added on 5/21/20 by pavelivanov
    // 409 code if backend can't perform the update because of subscription status
    // if (error === 'change_plan') {
    //   actions.models.router.push(links.cardUpdate)
    // }

    return Promise.reject()
  }
}

const _changeVindiciaPlan = (planName: string, placement: string) =>
  // TODO replace `plan` with `planName` or `name`
  request.post('rest/subscription/plan', {
    body: {
      plan: planName,
      placement,
    },
  })
    .then(async () => {
      actions.models.queue.fetch()

      try {
        const result = await fetch()

        // not sure if it correct (was taken from site/client/components/UpgradeSubscriptionFlow/SelectedPlanSummary/SelectedPlanSummary.js - added on 5/21/20 by pavelivanov
        if (result instanceof Error) {
          return Promise.reject()
        }
      }
      catch {
        return Promise.reject()
      }
    }, ({ statusCode, message, error, resData }) => {
      if (statusCode !== 409) {
        return Promise.reject(resData)
      }

      // 409 code if backend can't perform the update because of subscription status
      if (error === 'change_plan') {
        actions.models.router.push(links.cardUpdate)
      }

      return Promise.reject({ statusCode, message, error, resData })
    })

const changePlan = (planName: string, placement: string): Promise<void> => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    return _changeRecurlyPlan(planName, placement)
  }

  return _changeVindiciaPlan(planName, placement)
}


// ------------------------------------------------------------------------------------------

type SkipMonthsVariables = {
  input: {
    month: number
    limitedPause?: boolean
    remind?: boolean
  }
}

type SkipMonthsResponse = {
  skipMonth: {
    holdId: number
    refund?: {
      type: string
      status: string
      transactionType: 'PAUSE' | 'SKIP' | 'UNSUBSCRIBE'
    }
  }
}

type SkinMonthsModified = SkipMonthsResponse['skipMonth']

/**
 * Skip Months
 * @param {Object} data
 * @param {Number} data.monthCount
 * @param {Boolean} [data.limitedPause]
 * @param {Boolean} [data.remind]
 * @returns {Promise<any | never>}
 */
const skip = ({ monthCount, limitedPause = false, remind = false }) =>
  request.gql<SkipMonthsVariables, SkipMonthsResponse, SkinMonthsModified>('skipMonths', {
    body: {
      query: skipQuery,
      variables: {
        input: {
          month: monthCount,
          limitedPause,
          remind,
        },
      },
    },
    withNestedErrors: true,
    modifyResult: (data) => data.skipMonth,
  })
    .then(({ refund, holdId }) => (
      fetch()
        .then(() => ({ refund, holdId }))
    ))

const skipRecurly = async (input: Parameters<typeof skipSubscription>[0]) => {
  try {
    const { data, error } = await skipSubscription(input)

    if (error) {
      return Promise.reject(error)
    }

    await fetch()

    return data
  }
  catch (error) {
    if (error && error.message) {
      openErrorModal({ error: error.message })
    }

    console.error(error)

    return Promise.reject(error)
  }
}

// TODO: This FT is always on? Refine this and remove the logic.
const isUnskipFlowEnabled = getFeatureToggle(constants.features.unskipFlow)

/**
 *
 * @param {String} placement
 */
const _unskipVindicia = (placement) =>
  request.post('rest/subscription/unskip', {
    body: {
      placement,
    },
    modifyResult: modifySubscription,
  })
    .then(
      (result) => {
        const { notificationUserAboutPremiumProductInBatch, premiumProduct, status } = result

        // If the queue is locked notificationUserAboutPremiumProductInBatch: true
        // and server send first premiumProduct of current month, if he is exists.
        const showPremiumWelcomeBackModal  = notificationUserAboutPremiumProductInBatch && Boolean(premiumProduct)

        if (isUnskipFlowEnabled) {
          reducers.models.subscription.update(result)

          if (status === constants.subscriptionStatuses.unpaid) {
            actions.models.router.push(links.cardUpdate)
          }
          else {
            if (showPremiumWelcomeBackModal) {
              actions.models.modals.open(constants.modals.premiumProducts.WelcomeBack, {
                item: premiumProduct,
              })
            }
            else {
              actions.models.modals.open(constants.modals.UnskipSuccess)
            }

            actions.models.queue.fetch()
          }
        }
        else {
          if (showPremiumWelcomeBackModal) {
            actions.models.modals.open(constants.modals.premiumProducts.WelcomeBack, {
              item: premiumProduct,
            })
          }

          return fetch()
        }
      },
      ({ statusCode }) => {
        if (statusCode === 409 && isUnskipFlowEnabled) {
          return fetch()
            .then(({ isUnpaid }) => {
              if (isUnpaid) {
                actions.models.router.push(links.cardUpdate)
              }
            })
        }
      })

const _unskipRecurly = async (input: Parameters<typeof unskipSubscription>[0]) => {
  try {
    const { data, error } = await unskipSubscription(input)

    if (error) {
      if (error.code === UnskipErrorCode.NOT_UNSKIPPABLE) {
        await fetch()
      }
      else if (error.code === BusinessErrorCode.PAYMENT_METHOD_NOT_FOUND) {
        actions.models.router.push(links.cardUpdate)
      }

      return Promise.reject(error)
    }

    const { status } = await fetch()

    // If the queue is locked notificationUserAboutPremiumProductInBatch: true
    // and server send first premiumProduct of current month, if he is exists.
    const premiumProduct = data && data.premiumProductInBatchNotification && data.premiumProductInBatchNotification.product

    if (status === constants.subscriptionStatuses.unpaid) {
      actions.models.router.push(links.cardUpdate)
    }
    else {
      if (premiumProduct) {
        actions.models.modals.open(constants.modals.premiumProducts.WelcomeBack, {
          item: premiumProduct,
        })
      }
      else {
        actions.models.modals.open(constants.modals.UnskipSuccess)
      }

      actions.models.queue.fetch()
    }

    return data
  }
  catch (error) {
    console.error(error)

    return Promise.reject(error)
  }
}

const unskip = (placement) => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    const input = {
      metadata: { placement },
    }

    return _unskipRecurly(input)
  }

  return _unskipVindicia(placement)
}

/**
 *
 * @param {Boolean} short - return 5 reasons for new flow
 * @returns {*}
 */
const fetchUnsubscribeReasons = (short) =>
  request.get('rest/subscription/unsubscribeReasons', {
    query: {
      short,
    },
  })

/**
 * @description: This method used in RefundModal. He have 2 btns: refund & receive.
 * Backend needs to know what the user has chosen.
 * constants: subscriptionRefundTypes
 * @param {Object} opts - one of 'RECEIVE' || 'REFUND'
 * @param {string} opts.decision - one of 'RECEIVE' || 'REFUND'
 * @param {string} opts.transactionType - one of 'PAUSE' || 'SKIP' || 'UNSUBSCRIBE'
 */
const refund = ({ decision, transactionType }) =>
  request.post('rest/subscription/refund', {
    body: {
      decision,
      transactionType,
    },
  })

/**
 * @param {Object} values
 * @param {String} values.reason - reason name
 * @param {String} [values.comment] - user input
 * @returns {Promise<any>}
 */
const unsubscribe = (values) => {
  const {
    models: {
      subscription: { subscriptionDate },
      me: {
        marketing: {
          firstTouch: { medium },
        },
      },
    },
  } = getState()

  actions.models.ui.showRequestLoader()

  return request.post('rest/subscription/unsubscribe', {
    body: values,
    tokenName: constants.requestTokens.unsubscribe,
  })
    .then(async (data) => {
      dataLayer.track('unsubscribe', {
        partner: medium,
        subscriptionDate,
      })

      fbq.track('unsubscribe')

      await Promise.all([
        fetch(),
        actions.models.queue.fetch(),
      ])

      // ATTN reducers must be called after fetch() or state changes after fetch() will not be applied
      reducers.models.subscription.setUnpaused(false)
      reducers.models.subscription.setJustCancelled(true)

      return data
    })
    .finally(() => actions.models.ui.hideRequestLoader())
}

type UnsubscribeRecurlyProps = Parameters<typeof subscriptionUnsubscribe>[0]
type UnsubscribeRecurlyReturnType = SubscriptionUnsubscribeResult['data'] | SubscriptionUnsubscribeResult['error']

const unsubscribeRecurly = async (input: UnsubscribeRecurlyProps): Promise<UnsubscribeRecurlyReturnType> => {
  const {
    models: {
      subscription: { subscriptionDate },
      me: {
        marketing: {
          firstTouch: { medium },
        },
      },
    },
  } = getState()

  try {
    const { data, error } = await subscriptionUnsubscribe(input)

    if (error) {
      return Promise.reject(error)
    }

    dataLayer.track('unsubscribe', {
      partner: medium,
      subscriptionDate,
    })

    fbq.track('unsubscribe')

    await Promise.all([
      fetch(),
      actions.models.queue.fetch(),
    ])

    // ATTN reducers must be called after fetch() or state changes after fetch() will not be applied
    reducers.models.subscription.setUnpaused(false)
    reducers.models.subscription.setJustCancelled(true)

    return data
  }
  catch (error) {
    console.error(error)

    return Promise.reject()
  }
}

const rateResubscribe = (rate) =>
  request.post('rest/subscription/unsubscribe/rate', {
    body: {
      resubscribeRate: rate,
    },
  })


// -------------------------------------------------------------------------------------------------

type ResubscribeData = {
  plan?: string
  popup?: string
  monthlyPrice?: string
  resubscribeCode?: string
  successModal?: string
  withSuccessModal?: boolean
  withErrorHandler?: boolean
  withErrorRedirect?: boolean
  withCheck?: boolean
}

const _resubscribeRecurly = async (data: ResubscribeData) => {
  const {
    plan, popup, monthlyPrice, resubscribeCode,
    withSuccessModal = true, withErrorHandler = true, withCheck = true,
    successModal = constants.modals.resubscribe.ResubscribeSuccess,
  } = data || {}

  const { models: { subscription: { canResubscribe } } } = getState()

  if (withCheck && !canResubscribe) {
    actions.misc.cookies.setSessionItem(constants.cookieNames.resubscribeCode, resubscribeCode)

    actions.models.cart.addSubscription()
      .then(() => {
        const url = `${links.subscribe}${resubscribeCode ? `?resubscribeCode=${resubscribeCode}` : ''}`

        actions.models.router.push(url)
      })

    return Promise.reject({
      statusCode: 409,
      error: 'Can\'t resubscribe',
      isRedirected: true,
    })
  }

  try {
    actions.models.ui.showRequestLoader()

    const { data, error } = await resubscribeAction({
      plan,
      couponCode: resubscribeCode,
      metadata: {
        placement: popup,
      },
    }, {
      withErrorHandler,
    })

    if (error) {
      if (Object.values(BusinessErrorCode).includes(error.code as BusinessErrorCode)) {
        sentry.captureMessage('resubscribe business error', {
          extra: error,
        })
      }

      return Promise.reject(error)
    }

    const { premiumProductInBatchNotification } = data

    actions.misc.cookies.removeItem(constants.cookieNames.resubscribeCode)

    if (plan) {
      actions.models.queue.fetch()
    }

    await fetch()

    if (!withSuccessModal) {
      return data
    }

    if (premiumProductInBatchNotification) {
      actions.models.modals.open(constants.modals.premiumProducts.WelcomeBack, {
        item: premiumProductInBatchNotification.product,
      })
    }
    else {
      actions.models.modals.open(successModal, { monthlyPrice })
    }

    return data
  }
  catch (error) {
    console.error(error)

    return Promise.reject()
  }
  finally {
    actions.models.ui.hideRequestLoader()
  }
}

const _resubscribeVindicia = (data: ResubscribeData) => {
  const {
    plan,
    popup,
    monthlyPrice,
    resubscribeCode = '',
    withSuccessModal = true,
    withErrorHandler = true,
    successModal = constants.modals.resubscribe.ResubscribeSuccess,
  } = data || {}

  const { models: { subscription: { canResubscribe } } } = getState()

  if (!canResubscribe) {
    actions.misc.cookies.setSessionItem(constants.cookieNames.resubscribeCode, resubscribeCode)

    actions.models.cart.addSubscription()
      .then(() => {
        const url = `${links.subscribe}?resubscribeCode=${resubscribeCode}`

        actions.models.router.push(url)
      })

    return Promise.reject({
      statusCode: 409,
      error: 'Can\'t resubscribe',
      isRedirected: true,
    })
  }

  actions.models.ui.showRequestLoader()

  return request.post('rest/subscription/resubscribe', {
    body: {
      resubscribeCode,
      plan,
      popup,
    },
    tokenName: constants.requestTokens.resubscribe,
    withErrorModal: false,
    modifyResult: ({ notificationUserAboutPremiumProductInBatch, premiumProduct, ...rest }) => ({
      ...rest,
      // If the queue is locked notificationUserAboutPremiumProductInBatch: true
      // and server send first premiumProduct of current month, if it exists.
      premiumProduct: notificationUserAboutPremiumProductInBatch ? premiumProduct : null,
    }),
  })
    .then(
      async (result) => {
        const { premiumProduct } = result || {}

        actions.misc.cookies.removeItem(constants.cookieNames.resubscribeCode)

        if (plan) {
          actions.models.queue.fetch()
        }

        await fetch()

        if (!withSuccessModal) {
          return result
        }

        if (premiumProduct) {
          actions.models.modals.open(constants.modals.premiumProducts.WelcomeBack, {
            item: premiumProduct,
          })
        }
        else {
          actions.models.modals.open(successModal, { monthlyPrice })
        }

        return result
      },
      async ({ statusCode, error, resData, resData: { redirectUrl } }) => {
        let isRedirected = false

        // redirectUrl may be "/card-update?resubscribeCode=code" or "/card-update" only
        if (redirectUrl) {
          isRedirected = true
          actions.misc.cookies.setSessionItem(constants.cookieNames.resubscribeCode, resubscribeCode)

          actions.models.cart.addSubscription()
            .then(() => actions.models.router.push(redirectUrl, true))
        }
        else if (error === 'subscribed') {
          if (plan) {
            actions.models.queue.fetch()
          }

          await fetch()

          // resolve promise because user has been resubscribed already but has not updated subscription model for some reason
          return resData
        }
        else if (statusCode === 409 && withErrorHandler) {
          isRedirected = true
          actions.misc.cookies.setSessionItem(constants.cookieNames.resubscribeCode, resubscribeCode)

          actions.models.cart.addSubscription()
            .then(() => {
              const url = `${links.subscribe}?resubscribeCode=${resubscribeCode}`
              actions.models.router.push(url)
            })
        }

        return Promise.reject({ statusCode, error, isRedirected })
      }
    )
    .finally(() => actions.models.ui.hideRequestLoader())
}

const resubscribe = (data: ResubscribeData) => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    return _resubscribeRecurly(data)
  }

  return _resubscribeVindicia(data)
}


// ------------------------------------------------------------------------------------------

export type PlanCostsResult = { // ATTN this is current format (non Recurly)
  upgradeActivationDate: string // "2020-05"
  nextBillingDate: string
  balance: number // 1495
  tax: number // 100
  price: number
  inquirePlan: {
    name: string // "QUARTER"
    billingPeriod: number // 3
    shippingPeriod: number // 1
    monthCount: number // 3
    productsPerPeriod: number // 3
    productCount: number // 3
    totalPrice: number // 4350
    productPrice: number // 1450
    selected?: boolean // false
  }
}

const _fetchRecurlyPlanCosts = async (planName: string): Promise<PlanCostsResult> => {
  try {
    const { data, error } = await getSubscriptionPlanCosts({ targetPlan: planName })

    if (error) {
      console.error(error)

      openErrorModal({ error: error.message })

      return Promise.reject()
    }

    const { items: { tax }, inquirePlan, upgradeActivation, nextBillingDate, currentBalance, total } = data

    return Promise.resolve({
      inquirePlan: {
        ...inquirePlan,
        monthCount: inquirePlan.billingPeriod,
        totalPrice: inquirePlan.price,
      },
      upgradeActivationDate: upgradeActivation,
      nextBillingDate,
      balance: currentBalance,
      tax: tax ? tax.amount : 0,
      price: total,
    })
  }
  catch (axiosError) {
    console.error(axiosError.errors)

    return Promise.reject()
  }
}

const _fetchVindiciaPlanCosts = (planName): Promise<PlanCostsResult> =>
  request.get('rest/subscription/plan/inquire', {
    query: {
      plan: planName,
    },
    // Case on the Thank you page if user will update subscription from navigation menu
    multiple: true,
  })
    .then(({ nextBillingYearMonth, ...result }) => ({
      ...result,
      nextBillingYearMonth,
      nextBillingDate: nextBillingYearMonth,
    }))

const fetchPlanCosts = (planName: string): Promise<PlanCostsResult> => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    return _fetchRecurlyPlanCosts(planName)
  }

  return _fetchVindiciaPlanCosts(planName)
}


// ------------------------------------------------------------------------------------------

/**
 *
 * @param {String} planName
 */
const getPlanCost = (planName) =>
  request.get('rest/subscription/upgradeInquire', {
    query: {
      plan: planName,
    },
  })

/**
 *
 * @param {Object} values
 * @param {string} values.placement
 * @param {function} [values.onPlansFetched]
 * @param {function} [values.onSuccess]
 */
const handleUpgradeClick = (values) => {
  const { models: { subscription: { isUpgradable } } } = getState()

  if (isUpgradable) {
    actions.models.modals.open(constants.modals.UpgradeSubscription, values)
  }
  else {
    actions.models.router.push(links.profile.subscriptionStatus)
  }
}

const upgradePlan = ({ size: plan, placement }) => {
  actions.models.ui.showRequestLoader()

  return request.post('rest/subscription/upgrade', {
    body: {
      plan,
      placement,
      token: constants.requestTokens.upgrade,
    },
    tokenName: 'upgrade',
  })
    .then((data) => {
      reducers.models.subscription.setCurrentPlan(data.plan)
      return data
    })
    .finally(() => actions.models.ui.hideRequestLoader())
}

const applyCoupon = (couponCode: string): Promise<void> => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    return applyCouponAction({
      couponCode,
      origin: OriginType.SITE,
    })
      .then(({ error }) => {
        if (error) {
          openErrorModal({ error: error.message })

          return Promise.reject()
        }
      })
  }

  return request.post('rest/subscription/coupon', {
    body: {
      couponCode,
    },
    tokenName: constants.requestTokens.applyCouponForSubscription,
  })
}

const redeemSubscribed = (code) => {
  actions.models.ui.showRequestLoader()

  return request.post('rest/gift/subscription/redeemSubscribed', {
    body: {
      code,
    },
    tokenName: constants.requestTokens.redeemGiftSubscription,
  })
    .then((data) => {
      actions.models.queue.fetch()
      actions.models.modals.open(constants.modals.RedeemSuccess)

      reducers.models.me.activateGiftSubscription()

      return data
    })
    .finally(() => actions.models.ui.hideRequestLoader())
}

const fetchDataAfterPause = () => {
  fetch()
  actions.models.queue.fetch()
}

const pause = () =>
  request.post('rest/subscription/pause')
    .then((data) => {
      const { refund } = data || {}

      return refund
    })

/**
 * Unpauses subscription
 * discount may be unpauseType OR freeCase - not both
 *
 * @param {Object}  data
 * @param {String}  [data.unpauseCode]
 * @param {Boolean} [data.freeCase]
 * @param {String}  data.placement
 * @param {Boolean} [data.isCancellationFlow]
 * @param {Boolean} [data.withSuccessHandler]
 */
const _unpauseVindicia = (data) => {
  const { models: { me: { gender } } } = getState()
  const mainPageLink = gender === 'male' ? links.subscription.mainMen : links.subscription.mainWomen

  const { unpauseCode, placement, isCancellationFlow, freeCase, withSuccessHandler = true } = data || {}

  actions.models.ui.showRequestLoader()

  return request.post('rest/subscription/unpause', {
    body: {
      unpauseCode,
      freeCase,
      placement,
    },
    tokenName: constants.requestTokens.unpause,
  })
    .then((result) => {
      const { notificationUserAboutPremiumProductInBatch, premiumProduct } = result

      if (withSuccessHandler) {
        if (isCancellationFlow) {
          reducers.models.subscription.setUnpaused(true)
        }
        else {
          // If the queue is locked notificationUserAboutPremiumProductInBatch: true
          // and server send first premiumProduct of current month, if it exists.
          if (notificationUserAboutPremiumProductInBatch && premiumProduct) {
            actions.models.modals.open(constants.modals.premiumProducts.WelcomeBack, {
              item: premiumProduct,
            })
          }

          let query = '?notification=unpause-success'

          if (unpauseCode) {
            query += `&unpauseCode=${unpauseCode}`
          }

          actions.models.router.push(`${mainPageLink}${query}`)
        }
      }

      fetchDataAfterPause()
    }, ({ statusCode, error }) => {
      if (statusCode !== 409 || error === 'unexpected_error') {
        return Promise.reject()
      }

      if (error === 'not_subscribed') {
        fetch()

        return Promise.reject()
      }

      actions.misc.cookies.setSessionItem(constants.cookieNames.unpauseData, data)
      actions.models.router.push(links.cardUpdate)
    })
    .finally(() => actions.models.ui.hideRequestLoader())
}

const _unpauseRecurly = async ({ input, isCancellationFlow, withSuccessHandler = true }) => {
  const { models: { me: { gender } } } = getState()
  const mainPageLink = gender === 'male' ? links.subscription.mainMen : links.subscription.mainWomen

  try {
    actions.models.ui.showRequestLoader()

    const { data, error } = await unskipSubscription(input)

    if (error) {
      if (error.code === UnskipErrorCode.NOT_UNSKIPPABLE) {
        await fetch()
      }
      else if (error.code === BusinessErrorCode.PAYMENT_METHOD_NOT_FOUND) {
        actions.misc.cookies.setSessionItem(constants.cookieNames.unpauseData, data)
        actions.models.router.push(links.cardUpdate)
      }

      return Promise.reject(error)
    }

    const { status } = await fetch()

    if (withSuccessHandler) {
      if (isCancellationFlow) {
        reducers.models.subscription.setUnpaused(true)
      }
      else if (status === constants.subscriptionStatuses.unpaid) {
        actions.models.router.push(links.cardUpdate)
      }
      else {
        // If the queue is locked notificationUserAboutPremiumProductInBatch: true
        // and server send first premiumProduct of current month, if he is exists.
        const premiumProduct = data && data.premiumProductInBatchNotification && data.premiumProductInBatchNotification.product

        if (premiumProduct) {
          actions.models.modals.open(constants.modals.premiumProducts.WelcomeBack, {
            item: premiumProduct,
          })
        }

        let query = '?notification=unpause-success'

        if (input.couponCode) {
          query += `&unpauseCode=${input.couponCode}`
        }

        actions.models.router.push(`${mainPageLink}${query}`)
      }
    }

    actions.models.queue.fetch()
  }
  catch (error) {
    console.error(error)

    return Promise.reject(error)
  }
  finally {
    actions.models.ui.hideRequestLoader()
  }
}

export type UnpauseData = {
  unpauseCode?: UnpauseTypes
  placement: string
  redirectLink?: string
  isCancellationFlow?: boolean
  withSuccessHandler?: boolean
  freeCase?: boolean // legacy, should be couponCode in recurly
}

const unpause = (data: UnpauseData): Promise<void> => {
  const { models: { me: { paymentProvider } } } = getState()

  if (paymentProvider === 'RECURLY') {
    const { unpauseCode, placement, isCancellationFlow, withSuccessHandler = true } = data

    const input = {
      couponCode: unpauseCode,
      metadata: { placement },
    }

    return _unpauseRecurly({ input, isCancellationFlow, withSuccessHandler })
  }

  return _unpauseVindicia(data)
}

/**
 *
 * @param {Boolean} value
 */
const setDowngrade = (value) => reducers.models.subscription.setDowngrade(value)

const getDowngradePlan = async (state?: ReduxState) => {
  let { models: { subscription: { downgradePlan, currPlan: { shippingPeriod } } } } = state || getState()

  if (downgradePlan && downgradePlan.planName) {
    return downgradePlan
  }

  try {
    const plans = await fetchPlansSizes({ productsPerPeriod: 1, shippingPeriod })

    downgradePlan = plans.find((plan) => plan.shippingPeriod === shippingPeriod) || {}
  }
  catch (err) {
    sentry.captureException(`subscription actions.js getDowngradePlan ${err}`)

    downgradePlan = {}
  }

  reducers.models.subscription.setDowngradePlan(downgradePlan)

  return downgradePlan
}

const downgrade = async () => {
  const { planName } = await getDowngradePlan()

  if (planName) {
    try {
      await changePlan(planName, 'downgrade')

      // TODO recurly. disabled bcs it became redundant with Recurly changes (changePlan returns nothing) - added on 6/23/20 by pavelivanov
      // const response = await changePlan(planName, 'downgrade')
      //
      // if (response instanceof Error) {
      //   const error = (response && response.stack) ? response.stack : response
      //   sentry.captureException('Error in changePlan() + extra', {
      //     extra: {
      //       error,
      //     },
      //   })
      //
      //   return Promise.reject(response)
      // }

      mixpanel.track(constants.analyticsEvents.downgrade)
      setDowngrade(false)
    }
    catch (err) {
      const error = (err && err.stack) ? err.stack : err

      sentry.captureException('Error in changePlan() + extra', {
        extra: {
          error,
        },
      })

      return Promise.reject(err)
    }
  }
}

const setPrice = reducers.models.subscription.setPrice


export default {
  fetch,
  fetchCoupon,
  fetchPlans,
  fetchPlanCosts,
  fetchPlansSizes,
  fetchFrequencies,
  fetchCurrentPlan,
  fetchDataAfterPause,
  fetchUnsubscribeReasons,
  fetchCurrentPlanProductPrice,
  fetchFrequenciesForResubscribeUser,

  set,
  unskip,
  refund,
  pause,
  unpause,
  setPrice,
  downgrade,
  changePlan,
  skip,
  skipRecurly,
  upgradePlan,
  applyCoupon,
  unsubscribe,
  unsubscribeRecurly,
  resubscribe,
  getPlanCost,
  updateStatus,
  setDowngrade,
  rateResubscribe,
  getDowngradePlan,
  redeemSubscribed,
  handleUpgradeClick,
}
